import { capitalize, first, sortBy } from 'lodash';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Selector } from 'sonata-react-components';

import type React from 'react';

import './ScoringInfoModal.styles.scss';

type QualityScoring = {
  featuresQualityScoring?: Array<{
    feature: string;
    featureEntryQualityScoring?: Array<{
      entry: string;
      goalEventPercentage: number;
      sourceEventPercentage: number;
    }>;
  }>;
  kpiGoalEventCount?: number;
  kpiSourceEventCount?: number;
  modelCampaignsCount?: number;
};

type ScoringInfoModalProps = {
  qualityScoring: QualityScoring;
};

type State = {
  selectedFeature: string;
};

/**
 * Renders the Quality Scoring Info content inside the 550px modal.
 * Shows a summary table (Campaign Count, KPI Source Event Count, KPI Goal Event Count)
 * and a per-feature selector with detailed quality scoring data.
 */
function ScoringInfoModal({ qualityScoring }: ScoringInfoModalProps) {
  const { formatMessage } = useIntl();

  const firstFeature = first(qualityScoring.featuresQualityScoring ?? []);

  const [{ selectedFeature }, setState] = useState<State>({
    selectedFeature: firstFeature?.feature ?? ''
  });

  const getInfoTableRows = useCallback(
    (rows: Array<{ data: React.ReactNode[]; label: string }>, includeHeader: boolean) => {
      return (
        <table
          className={`scoringInfoModal__infoTable ${includeHeader ? 'scoringInfoModal__infoTable--withHeader' : ''}`}
        >
          {includeHeader && (
            <thead>
              <tr className="scoringInfoModal__headTableRow" key={0}>
                <th className="scoringInfoModal__headTableCell">{rows[0].label}</th>
                {rows[0].data.map((headCell, i) => (
                  <th className="scoringInfoModal__headTableCell" key={`0_${i}`}>
                    {headCell}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.slice(includeHeader ? 1 : 0).map(({ data, label }, i) => (
              <tr className="scoringInfoModal__infoTableRow" key={i}>
                <td className="scoringInfoModal__infoTableCell">{label}</td>
                {data.map((dataCell, j) => (
                  <td className="scoringInfoModal__infoTableCell" key={`${i}_${j}`}>
                    {dataCell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    },
    []
  );

  const selectedFeatureData = qualityScoring.featuresQualityScoring?.find(f => f.feature === selectedFeature);

  return (
    <div className="scoringInfoModal">
      <div className="scoringInfoModal__table">
        <div className="scoringInfoModal__tableHeader">
          {formatMessage({
            defaultMessage: 'Selected Optimization Model Info',
            id: 'optimizationModel.campaignOptimizationModelTooltipHeader'
          })}
        </div>
        {getInfoTableRows(
          [
            {
              data: [qualityScoring.modelCampaignsCount],
              label: formatMessage({
                defaultMessage: 'Campaign Count',
                id: 'optimizationModel.infoModal.modelCampaignsCount'
              })
            },
            {
              data: [qualityScoring.kpiSourceEventCount],
              label: formatMessage({
                defaultMessage: 'KPI Source Event Count',
                id: 'optimizationModel.infoModal.kpiSourceEventCount'
              })
            },
            {
              data: [qualityScoring.kpiGoalEventCount],
              label: formatMessage({
                defaultMessage: 'KPI Goal Event Count',
                id: 'optimizationModel.infoModal.kpiGoalEventCount'
              })
            }
          ],
          false
        )}
      </div>
      {(qualityScoring.featuresQualityScoring ?? []).length > 0 && (
        <>
          <Selector
            className=""
            defaultValue={[
              {
                id: selectedFeature,
                label: capitalize(selectedFeature)
              }
            ]}
            label={formatMessage({
              defaultMessage: 'Select Feature:',
              id: 'optimizationModel.infoModal.featureSelectorLabel'
            })}
            onChange={(selectedOption: Record<string, any>) => {
              setState(prevState => ({
                ...prevState,
                selectedFeature: selectedOption.value
              }));
            }}
            options={(qualityScoring.featuresQualityScoring ?? []).map(featureData => ({
              id: featureData.feature,
              label: capitalize(featureData.feature)
            }))}
            showSearch
            type="simple"
          />
          {selectedFeatureData?.featureEntryQualityScoring && (
            <div className="scoringInfoModal__table">
              <div className="scoringInfoModal__tableHeader">
                {formatMessage({
                  defaultMessage: 'Selected Feature Info',
                  id: 'optimizationModel.infoModal.campaignOptimizationModelQualityInfoFeatureHeader'
                })}
              </div>
              {getInfoTableRows(
                [
                  {
                    data: [
                      formatMessage({
                        defaultMessage: 'Source Event %',
                        id: 'optimizationModel.infoModal.sourceEventPercentageHeader'
                      }),
                      formatMessage({
                        defaultMessage: 'Goal Event %',
                        id: 'optimizationModel.infoModal.goalEventPercentageHeader'
                      })
                    ],
                    label: formatMessage({
                      defaultMessage: 'Entry',
                      id: 'optimizationModel.infoModal.entryHeader'
                    })
                  },
                  ...sortBy(selectedFeatureData.featureEntryQualityScoring, 'entry').map(entryData => ({
                    data: [
                      `${Number.isNaN(entryData.sourceEventPercentage) ? 0 : entryData.sourceEventPercentage.toFixed(2)}%`,
                      `${Number.isNaN(entryData.goalEventPercentage) ? 0 : entryData.goalEventPercentage.toFixed(2)}%`
                    ],
                    label: capitalize(entryData.entry)
                  }))
                ],
                true
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ScoringInfoModal;
